// CountUp Components (version améliorée)
export const CountUp = (() => {
	const isMobile = () => window.matchMedia("(max-width: 768px)").matches;

	const defaults = {
		selector: ".key-metric__number",
		startAttr: "data-count-start",
		endAttr: "data-count-end",
		durationAttr: "data-count-duration",
		threshold: 0.2,
		once: true,
		locale: null,
		decimals: 0,
		valueClass: "value",
		unitClass: "unit",
		initWithStart: true,
		easing: "easeOutCubic", // <--- nouvel ajout
	};

	// EASING FUNCTIONS
	const easings = {
		linear: (t) => t,
		easeOutCubic: (t) => 1 - Math.pow(1 - t, 3),
		easeOutQuad: (t) => t * (2 - t),
		easeOutQuint: (t) => 1 - Math.pow(1 - t, 5),
		easeOutExpo: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
	};

	const parseValue = (val) => {
		const match = /^([\d.,]+)(.*)$/.exec(val.trim());
		return {
			number: parseFloat(match?.[1].replace(",", ".")) || 0,
			unit: match?.[2] || "",
		};
	};

	// Helper to format numbers per requirements
	const customFormat = (num, config, { suppressGrouping = false } = {}) => {
		// Suppress group separator for "year-like" values between 1990 and 2100
		if (Math.abs(num) > 1990 && Math.abs(num) < 2100) {
			return Number(num).toFixed(config.decimals);
		}
		const formatter = new Intl.NumberFormat(
			config.locale || document.documentElement.lang || "en-US",
			{
				minimumFractionDigits: config.decimals,
				maximumFractionDigits: config.decimals,
				useGrouping: suppressGrouping ? false : true,
			}
		);
		return formatter.format(num);
	};

	const prepareElement = (el, config) => {
		if (el.querySelector(`.${config.valueClass}`)) return;

		const startParsed = parseValue(el.getAttribute(config.startAttr) || "0");
		const endParsed = parseValue(el.getAttribute(config.endAttr) || "0");

		const unit = endParsed.unit || startParsed.unit;

		const valueSpan = document.createElement("span");
		valueSpan.className = config.valueClass;
		valueSpan.textContent = config.initWithStart
			? customFormat(startParsed.number, config)
			: "0";

		const unitSpan = document.createElement("span");
		unitSpan.className = config.unitClass;
		unitSpan.textContent = unit;

		el.textContent = "";
		el.appendChild(valueSpan);
		if (unit) el.appendChild(unitSpan);
	};

	// ---- ANIMATION ----
	const animate = (el, config) => {
		//  Sur mobile, ne pas relancer si déjà animé
		if (isMobile() && el.dataset.animated === "true") return;

		const startParsed = parseValue(el.getAttribute(config.startAttr) || "0");
		const endParsed = parseValue(el.getAttribute(config.endAttr) || "0");

		const start = startParsed.number;
		const end = endParsed.number;
		const unit = endParsed.unit || startParsed.unit;

		let duration = parseInt(el.getAttribute(config.durationAttr)) || 2000;

		// Accélération sur mobile (ex: x1.7 plus rapide)
		if (isMobile()) {
			duration = duration * 0.6; // 60% du temps original
		}

		const valueSpan = el.querySelector(`.${config.valueClass}`);
		const unitSpan = el.querySelector(`.${config.unitClass}`);
		if (unitSpan) unitSpan.textContent = unit;

		const ease = easings[config.easing] || easings.linear;

		let startTimestamp = null;

		const step = (ts) => {
			if (!startTimestamp) startTimestamp = ts;

			const raw = Math.min((ts - startTimestamp) / duration, 1);
			const progress = ease(raw);

			const value = start + (end - start) * progress;

			// DURING ANIMATION: always suppress grouping for "year-like" values (1990-2100) (e.g., 2,006/2 006 should be 2006)
			if (raw < 1) {
				valueSpan.textContent = customFormat(value, config, { suppressGrouping: true });
			} else {
				// Snap final clean
				valueSpan.textContent = customFormat(end, config);
				// Flag uniquement mobile
				if (isMobile()) el.dataset.animated = "true";
			}
			if (raw < 1) requestAnimationFrame(step);
		};

		requestAnimationFrame(step);
	};

	// ---- INIT ----
	const init = (userOptions = {}) => {
		const config = { ...defaults, ...userOptions };
		const elements = document.querySelectorAll(config.selector);
		console.log(config);
		if (!elements.length) return;

		elements.forEach((el) => prepareElement(el, config));

		// MOBILE → tout lancer en même temps
		if (isMobile()) {
			const triggerEl = elements[0].closest(".key-figures") || elements[0];

			const observer = new IntersectionObserver(
				(entries, obs) => {
					if (entries[0].isIntersecting) {
						elements.forEach((el) => animate(el, config));
						obs.disconnect();
					}
				},
				{ threshold: 0.1 }
			);

			observer.observe(triggerEl);
			return;
		}

		const observer = new IntersectionObserver(
			(entries, obs) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						animate(entry.target, config);
						if (config.once) obs.unobserve(entry.target);
					}
				});
			},
			{ threshold: config.threshold }
		);

		elements.forEach((el) => observer.observe(el));
	};

	return { init };
})();
